import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { FormattedMessage } from 'react-intl';

import { useDispatch } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import {
  BtnBar,
  CtaBtn,
  CustomSelect,
  DangerAlert,
  Form,
  FormItem,
  FormRow,
  InfoAlert,
  Input,
  SecondaryBtn,
  Textarea,
  WaitingAlert,
  SuccessAlert,
  WarningAlert,
} from '@tmapy/style-guide';

import type {
  NestedAttachmentResponse,
  NestedGeodesistResponse,
  PatchedDtmDocUpdateRequest,
  UserListResponse,
} from '../../openapi/types';
import { FlowEnum } from '../../openapi/types/FlowEnum';
import {
  DocsRetrieveResponse,
  useDocsRetrieveResource,
} from '../../openapi/operations/docsRetrieve';
import { geodesistsList } from '../../openapi/operations/geodesistsList';
import { dtmKadDocsPartialUpdate } from '../../openapi/operations/dtmKadDocsPartialUpdate';
import { docsList, docsListOperation } from '../../openapi/operations/docsList';
import { dtmKadDocsCreate } from '../../openapi/operations/dtmKadDocsCreate';
import { dtmKadDocsDestroy } from '../../openapi/operations/dtmKadDocsDestroy';
import { dtmKadDocsSendToDmvsPartialUpdate } from '../../openapi/operations/dtmKadDocsSendToDmvsPartialUpdate';
import { dtmKadDocsCancelPartialUpdate } from '../../openapi/operations/dtmKadDocsCancelPartialUpdate';
import { dtmKadDocsCheckDmvsPendingPozadavkyCreate } from '../../openapi/operations/dtmKadDocsCheckDmvsPendingPozadavkyCreate';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';

import { HistoryList } from '../../history/components/HistoryList';
import type { InputProps } from '../../hooks/useInput';
import {
  SSE_TYPE,
  useAppContext,
  useAuth,
  useFormItemServerErrors,
  useHistoryAction,
  useInput,
  useLogDownloadLink,
  useSelect,
  useSelector,
  useSSE,
  useAction,
  toBtnQueryState,
} from '../../hooks';

import { RouteId, routeToUrl, RouteUrlGetterParams } from '../../routes';
import { APP_TYPE, QUERY_STATE } from '../../constants';
import { actionResourceInvalidate } from '../../redux/resource/actions';

import {
  ATTACHMENT_TYPES,
  DOC_STATE,
  DOCDETAIL_LOAD_SUCCESS,
  DOCDETAIL_LOAD_WAITING,
} from '../constants';
import { getFormValues } from '../utils/getformvalues';

import { docFormMessages, docTitleMessages } from './docsMessages';
import { docEditButtonsMsg, docEditMessages, validatorMessages } from './docEditMessages';

import { defaultValues } from './defaultValues';
import { DocAttachments } from './DocAttachments';
import { DocBadge } from './DocBadge';
import { LogAlert } from './LogAlert';
import { useMonitoringTimeline } from '../../monitoring/hooks/useMonitoringTimeline';
import { LinkTooltip } from './LinkTooltip';
import { DocDownloadFiles } from './DocDownloadFiles';
import { DocInternalInfo } from './DocInternalInfo';

import './Doc.scss';

const renderUserSelectItem = (item: UserListResponse | NestedGeodesistResponse) => (
  <>
    {item.name} {item.surname}
  </>
);

export type DocKadEditContainerProps = {
  docId?: number;
};

export const DocDtmKad: React.FC<DocKadEditContainerProps> = ({ docId }) => {
  const monitoringSubject = useMemo(() => (docId ? { doc: docId } : null), [docId]);
  const timeline = useMonitoringTimeline(monitoringSubject, { withErrors: true });

  useSSE(SSE_TYPE.DOCS, docId, timeline.markStale);

  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { appType } = useAppContext();

  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [cancelQueryState, setCancelQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [destroyQueryState, setDestroyQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [sendToDMVSQueryState, setSendToDMVSQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const [checkDmvsPendingPozadavkyQueryState, setCheckDmvsPendingPozadavkyQueryState] =
    useState<QUERY_STATE>(QUERY_STATE.EMPTY);

  const [validate, setValidate] = useState(false);

  const serverErrors = useSelector((state) => state.docedit.errors);
  const removeQueryState = useSelector((state) => state.doc.removeQueryState);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const auth = useAuth();

  const stateData = useSelector((state) => state.doc.data);
  const resource = useDocsRetrieveResource(docId ? { id: docId } : null);

  const data = useMemo(() => {
    const cachedData = stateData.id === docId ? stateData : resource.data;
    return docId && cachedData
      ? cachedData
      : {
          ...defaultValues,
          state: DOC_STATE.INPROGRESS,
          flow: FlowEnum.DTM_KAD,
        };
  }, [resource.data, stateData, docId]);

  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('DtmKadDocs', 'partial_update', data),
      docsUpdate: auth.hasPermission('DtmKadDocs', 'update', data),
      docsDestroy: auth.hasPermission('DtmKadDocs', 'destroy', data),
      /**
       * polozka, ktera i pri disable formulare ve stavu DTM_EDITING muze byt zmenena a ulozena
       * ovlivnuje tedy viditelnost tlacitka ulozit
       * !musi byt spravne nastaveno pravidlo partial_update pro BE, aby PTACH probehl
       */
      docsActionCancel: auth.hasPermission('DtmKadDocs', 'action.cancel', data),
      docsActionSendToDMVS: auth.hasPermission('DtmKadDocs', 'action.send_to_dmvs', data),

      // download section
      docsActionDonwloadProtocol: auth.hasPermission('DtmKadDocs', 'download_r17_protocol', data),

      // restart dmvs pending
      docsActionCheckDmvsPendingPozadavky: auth.hasPermission(
        'DtmKadDocs',
        'check_dmvs_pending_pozadavky',
        data,
      ),
    }),
    [auth, data],
  );

  const routeDetail = useHistoryAction('podaniKADDetail', { id: docId! });
  const routeList = useHistoryAction('docList', { appType });

  const [showLogDownloadError, setShowLogDownloadError] = useState(false);
  const handleShowDownloadLogError = (visible: boolean) => {
    setShowLogDownloadError(visible);
  };

  const protocolDownloadLink = useLogDownloadLink(
    resource.data?.url,
    data.flow,
    'download_r17_protocol',
    handleShowDownloadLogError,
  );

  useEffect(() => {
    if (!resource.data) {
      dispatch({
        type: DOCDETAIL_LOAD_WAITING,
      });
    } else {
      dispatch({
        type: DOCDETAIL_LOAD_SUCCESS,
        data: resource.data,
      });
    }
  }, [resource.data, dispatch]);

  // ACTIONS
  const handleRefetch = useCallback(() => {
    resource.refetch();
  }, [resource]);

  const actionQuery = useMemo(() => ({ id: docId }), [docId]);
  const { run: doAction, isActionRunning } = useAction({
    query: actionQuery,
    onRefetch: resource.refetch,
    routeList,
    listPath: docsListOperation().path,
  });

  const handleSaveDocWithValidation = async () => {
    setValidate(true);

    const isSomeFormItemInvalid = Object.values(formItems).some((formItem: any) => {
      return formItem.validator?.(formItem.value);
    });

    if (!isSomeFormItemInvalid) {
      return await handleSaveDocWithoutValidation();
    }
    return null;
  };

  const handleSaveDocWithoutValidation = async () => {
    const formValues = getFormValues(formItems);
    const values = {
      ...formValues,
      // select
      geodesist: formValues.geodesist.url || '',
    };

    return await handleSave(values);
  };

  const handleSave = useCallback(
    async (values: PatchedDtmDocUpdateRequest) => {
      setSaveQueryState(QUERY_STATE.WAITING);
      let response;

      try {
        if (docId) {
          response = await dtmKadDocsPartialUpdate({
            query: { id: docId },
            request: values,
          });

          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);
            handleRefetch();
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        } else {
          response = await dtmKadDocsCreate({
            request: values,
          });
          if (response.ok) {
            setSaveQueryState(QUERY_STATE.SUCCESS);

            routeDetail.replace({ id: response.data.id });
          } else {
            setSaveQueryState(QUERY_STATE.FAIL);
          }
        }
      } finally {
        const docsList = docsListOperation();
        dispatch(actionResourceInvalidate(docsList.path));
      }
      if (response.ok) {
        const attachments = response.data.attachments ?? [];
        return {
          download_attachments_link: '',
          ...response.data,
          attachments,
        };
      }
      return null;
    },
    [docId, routeDetail, appType, dispatch],
  );

  const handleCancelBtnClick = () => {
    doAction(dtmKadDocsCancelPartialUpdate, setCancelQueryState);
  };

  const handleDeleteBtnClick = () => {
    doAction(dtmKadDocsDestroy, setDestroyQueryState, { refetch: false, redirectToList: true });
  };

  const handleSendToDMVSBtnClick = () => {
    const updateDataAttachments =
      data.attachments.filter(
        (item: NestedAttachmentResponse) => item.attachment_type === ATTACHMENT_TYPES.UPDATE_DATA,
      ) ?? [];
    const isInvalid = updateDataAttachments.some((item: NestedAttachmentResponse) => {
      const extension = item.file?.match(/\.([a-zA-Z0-9]+)$/)?.[1];
      return extension !== 'zip';
    });
    if (!isInvalid && updateDataAttachments.length > 0) {
      doAction(dtmKadDocsSendToDmvsPartialUpdate, setSendToDMVSQueryState);
    } else {
      setSendToDMVSQueryState(QUERY_STATE.FAIL);
    }
  };

  const handleCheckDmvsPendingPozadavkyBtnClick = () => {
    doAction(dtmKadDocsCheckDmvsPendingPozadavkyCreate, setCheckDmvsPendingPozadavkyQueryState);
  };

  // LINK TO DMVS GAD
  const [linkToDmvsGadWarningShow, setLinkToDmvsGadWarningShow] = useState(false);
  const handleLinkToDmvsGadBtnClick = useCallback(async () => {
    if (!data.id || !data.name) return;

    const respond = await docsList({
      query: {
        description__icontains: data.description || data.name,
        dmvs_identifikace_zmeny__icontains: String(data.id),
      },
    });

    if (respond.data?.results?.length === 1) {
      const getUrl = routeToUrl['dmvsGadDetail'] as (
        params?: RouteUrlGetterParams<RouteId>,
      ) => string;
      navigate(getUrl({ appType: APP_TYPE.MANAGER, id: respond.data.results[0].id }));
    } else {
      setLinkToDmvsGadWarningShow(true);
    }
  }, [data.id, data.name, data.description, navigate]);

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
    setValidate(false);
  }, [setValidate, setSaveQueryState]);

  // NAME
  const name = useInput<InputProps>({
    name: 'name',
    isDisabled: !acl.canUpdate,
    initialValue: data.name,
    label: formatMessage(docFormMessages.name),
    validate: validate,
    validator: (value) =>
      !value ? formatMessage(validatorMessages.buildingDescriptionError) : null,
    onChange: formItemChangeCallback,
    ...formItemServerErrors.name,
  });

  // DESCRIPTION
  const description = useInput<InputProps>({
    name: 'description',
    isDisabled: !acl.canUpdate,
    initialValue: data.description,
    label: formatMessage(docFormMessages.description),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.description,
  });

  // GEODESIST
  const prepareGeodesistItems = useCallback(async (search: string) => {
    const response = await geodesistsList({ query: { search } });
    if (response.ok) {
      return response.data?.results ?? null;
    }
    return null;
  }, []);
  const geodesist = useSelect<DocsRetrieveResponse['geodesist'], DocsRetrieveResponse['geodesist']>(
    {
      name: 'geodesist',
      isDisabled: !acl.canUpdate,
      label: formatMessage(docFormMessages.geodesist),
      initialValue: data.geodesist,
      onChange: formItemChangeCallback,
      prepareItems: prepareGeodesistItems,
      renderValue: (item) => (item.name || item.surname ? `${item.name} ${item.surname}` : ''),
      renderItem: renderUserSelectItem,
      ...formItemServerErrors.geodesist,
    },
  );

  const state = { value: data.state };
  const formItems = {
    state,
    name,
    description,
    geodesist,
  } as const;

  // ATTACHMENTS
  const uploadHelpText = formatMessage.fallback(['document.podaniKAD.uploadHelpText']);

  return (
    <MainContent width='full' key={`${resource.data?.id}`}>
      {(resource.queryState === QUERY_STATE.SUCCESS || !docId) && (
        <article>
          <div className='dmx-doc--head sg-u-vs-2'>
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-c sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-a-g-1 sg-a-fw-w'>
                {data.name && data.id && (
                  <LinkTooltip
                    title={`${formatMessage(docEditButtonsMsg.docBtnLinkToDmvsGad)} DMVS GAD`}
                    description=''
                    onClick={handleLinkToDmvsGadBtnClick}
                  />
                )}
                <PageTitle>
                  {formatMessage.lookup(docTitleMessages, 'podaniKAD', {
                    id: data.id || 'new',
                  })}
                </PageTitle>
                {data.state && (
                  <DocBadge
                    docState={data.state}
                    flow={data.flow ?? ''}
                    rejectedBySubjekty={data.rejected_by_subjekty}
                  />
                )}
              </header>
              <BtnBar>
                {acl.docsUpdate && (
                  <CtaBtn
                    queryState={toBtnQueryState(saveQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSaveDocWithValidation}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnSave} />
                  </CtaBtn>
                )}

                {!!docId && acl.docsActionSendToDMVS && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(sendToDMVSQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleSendToDMVSBtnClick}
                  >
                    {data.state === DOC_STATE.SEND_TO_DMVS_FAILED ? (
                      <FormattedMessage {...docEditButtonsMsg.docBtnRetry} />
                    ) : (
                      <FormattedMessage {...docEditButtonsMsg.docBtnSendToDMVS} />
                    )}
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCancel && (
                  <SecondaryBtn
                    isDanger
                    queryState={toBtnQueryState(cancelQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCancelBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCancel} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsDestroy && (
                  <SecondaryBtn
                    isDisabled={isActionRunning || removeQueryState === QUERY_STATE.WAITING}
                    isDanger
                    onClick={handleDeleteBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnRemove} />
                  </SecondaryBtn>
                )}

                {!!docId && acl.docsActionCheckDmvsPendingPozadavky && (
                  <SecondaryBtn
                    queryState={toBtnQueryState(checkDmvsPendingPozadavkyQueryState)}
                    isDisabled={isActionRunning}
                    onClick={handleCheckDmvsPendingPozadavkyBtnClick}
                  >
                    <FormattedMessage {...docEditButtonsMsg.docBtnCheckDmvsPendingPozadavky} />
                  </SecondaryBtn>
                )}
              </BtnBar>
            </div>
            {showLogDownloadError && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.fileDownloadError} />
              </DangerAlert>
            )}
            {(resource.queryState === QUERY_STATE.WAITING ||
              saveQueryState === QUERY_STATE.WAITING ||
              cancelQueryState === QUERY_STATE.WAITING ||
              destroyQueryState === QUERY_STATE.WAITING ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.WAITING ||
              sendToDMVSQueryState === QUERY_STATE.WAITING) && (
              <WaitingAlert>
                <FormattedMessage {...docEditMessages.queryStateWaiting} />
              </WaitingAlert>
            )}
            {(cancelQueryState === QUERY_STATE.FAIL ||
              checkDmvsPendingPozadavkyQueryState === QUERY_STATE.FAIL) && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.queryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateFail} />
              </DangerAlert>
            )}

            {destroyQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.destroyQueryStateFail} />
              </DangerAlert>
            )}

            {saveQueryState === QUERY_STATE.SUCCESS && (
              <SuccessAlert>
                <FormattedMessage {...docEditMessages.submitQueryStateSuccess} />
              </SuccessAlert>
            )}
            {formItemServerErrors.non_field_errors && (
              <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
            )}
            {resource.queryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.loadQueryStateFail} />
              </DangerAlert>
            )}
            {sendToDMVSQueryState === QUERY_STATE.FAIL && (
              <DangerAlert>
                <FormattedMessage {...docEditMessages.sendToDMVSQueryStateFail} />
              </DangerAlert>
            )}
            {linkToDmvsGadWarningShow && (
              <WarningAlert>
                <FormattedMessage {...docEditMessages.linkToDmvsGadWarning} />
              </WarningAlert>
            )}
            {data.state === DOC_STATE.SEND_TO_DMVS_FAILED && (
              <LogAlert log={timeline.latestError ?? undefined} />
            )}
          </div>
          <div className='dmx-doc--cols'>
            <div className='sg-u-vs-4'>
              <Form onSubmit={handleSaveDocWithValidation}>
                <div className='sg-u-vs-4'>
                  <section>
                    <h3 className='dmx-doc--attributesTitle sg-u-type-h2 sg-a-ml-2'>&nbsp;</h3>
                    <div className='sg-u-box sg-a-p-2 sg-u-vs-2 sg-a-mt-1'>
                      <FormRow>
                        <FormItem {...name} isRequired>
                          <Input {...name} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...description}>
                          <Textarea {...description} />
                        </FormItem>
                      </FormRow>
                      <FormRow>
                        <FormItem {...geodesist}>
                          <CustomSelect {...geodesist} />
                        </FormItem>
                      </FormRow>
                    </div>
                  </section>
                </div>
              </Form>

              {resource.data && <DocInternalInfo doc={resource.data} />}

              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2'>
                  <FormattedMessage {...docFormMessages.attachments} />
                </h3>
                <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-u-vs-3'>
                  <section>
                    <h3 className='sg-u-type-h2 sg-a-ml-2'>
                      <FormattedMessage {...docFormMessages.attachments_docs} />
                    </h3>
                    <div className='sg-u-box sg-a-d-ib sg-a-mw-100% sg-a-p-2 sg-a-mt-1 sg-u-vs-2'>
                      {uploadHelpText && (
                        <div className='sg-a-mw-fc'>
                          <InfoAlert>{uploadHelpText}</InfoAlert>
                        </div>
                      )}
                      <DocAttachments
                        doc={data}
                        prejimkaType={data.prejimka_type}
                        createDoc={handleSaveDocWithoutValidation}
                        attachments={stateData.attachments}
                      />
                    </div>
                  </section>
                  <section className='sg-a-mt-2'>
                    <DocDownloadFiles
                      title={<FormattedMessage {...docFormMessages.attachments_system} />}
                      docId={docId}
                      items={[
                        {
                          permission: acl.docsActionDonwloadProtocol,
                          link: protocolDownloadLink,
                          label: <FormattedMessage {...docEditButtonsMsg.docBtnDownloadProtocol} />,
                          ext: 'pdf',
                        },
                      ]}
                    />
                  </section>
                </div>
              </section>
            </div>
            <div className='dmx-doc--rightCol sg-u-vs-4'>
              <section>
                <h3 className='sg-u-type-h2 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...docFormMessages.history} />
                </h3>
                <HistoryList history={data.history} />
              </section>
            </div>
          </div>
        </article>
      )}
    </MainContent>
  );
};
